import { motion } from "framer-motion";
import { Calendar, Clock, ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";

export interface BlogPost {
  title: string;
  excerpt: string;
  date: string;
  category: string;
  readTime: string;
  image: string;
  href: string;
  featured?: boolean;
}

interface BlogCardProps {
  post: BlogPost;
  index?: number;
}

export function BlogCard({ post, index = 0 }: BlogCardProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.1 }}
      className={cn(
        "group relative flex flex-col h-full rounded-[2rem] overflow-hidden bg-white/[0.02] border border-white/5 hover:border-electric/30 transition-all",
        post.featured ? "md:flex-row md:col-span-2 lg:col-span-3" : ""
      )}
    >
      <div className={cn(
        "relative overflow-hidden aspect-video",
        post.featured ? "md:w-1/2 md:aspect-auto" : "w-full"
      )}>
        <img 
          src={post.image} 
          alt={post.title} 
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
          loading={index < 2 ? "eager" : "lazy"}
          decoding="async"
          {...(index < 2 && { fetchpriority: "high" } as any)}
        />
        <div className="absolute top-4 left-4">
          <span className="px-3 py-1 rounded-full bg-electric text-white text-[10px] font-black uppercase tracking-widest shadow-lg">
            {post.category}
          </span>
        </div>
      </div>

      <div className={cn(
        "flex flex-col flex-1 p-8",
        post.featured ? "md:w-1/2 justify-center" : ""
      )}>
        <div className="flex items-center gap-4 text-[10px] text-muted-foreground uppercase tracking-widest font-bold mb-4">
          <div className="flex items-center gap-1.5">
            <Calendar className="w-3 h-3 text-electric" />
            {post.date}
          </div>
          <div className="flex items-center gap-1.5">
            <Clock className="w-3 h-3 text-electric" />
            {post.readTime}
          </div>
        </div>

        <h3 className={cn(
          "font-black tracking-tighter leading-tight mb-4 group-hover:text-electric transition-colors",
          post.featured ? "text-2xl md:text-4xl" : "text-xl"
        )}>
          {post.title}
        </h3>

        <p 
          className="text-muted-foreground text-sm leading-relaxed mb-8 line-clamp-3 italic"
          dangerouslySetInnerHTML={{ 
            __html: post.excerpt.replace(/(Painel IPTV Pós Pago|Revenda IPTV|Servidor IPTV|Painel IPTV)/gi, (match) => 
              `<a href="/" class="text-electric font-bold hover:underline" onclick="event.stopPropagation()">${match}</a>`
            )
          }}
        />

        <div className="mt-auto">
          <a 
            href={post.href}
            className="inline-flex items-center gap-2 text-xs font-black uppercase tracking-widest text-white group-hover:gap-4 transition-all"
          >
            Ler Artigo Completo
            <ArrowRight className="w-4 h-4 text-electric" />
          </a>
        </div>
      </div>
    </motion.div>
  );
}
